import { computed, ref, watch, type Ref } from 'vue';
import type Conversation from '@/entities/Conversation.ts';
import type { Message } from '@/entities/types/ConversationType.ts';
import { adjustScrollAfterImages } from '@/components/sms/utils/adjustScrollAfterImages.ts';

interface UseSmsNewMessageIndicatorOptions {
    selectedConversation: Ref<Conversation | null | undefined>;
    selectedConversationMessages: Ref<Message[]>;
    messagesContainer: Ref<HTMLElement | undefined>;
    scrollToBottom: () => void;
}

export const useSmsNewMessageIndicator = (options: UseSmsNewMessageIndicatorOptions) => {
    const firstNewMessageId = ref<number | null>(null);
    const newMessageIds = ref<number[]>([]);

    const shouldShowNewMessageIndicator = computed(() => newMessageIds.value.length > 0);
    const newMessageCount = computed(() => newMessageIds.value.length);

    const dismiss = (): void => {
        firstNewMessageId.value = null;
        newMessageIds.value = [];
    };

    const captureIncomingMessagesWhileAway = (previousLastMessageId: number | null): void => {
        const messages = options.selectedConversationMessages.value;

        if (!messages.length) {
            return;
        }

        const newIncomingMessages = messages.filter((m) => {
            if (!m.isIncoming) {
                return false;
            }

            return previousLastMessageId === null || m.id > previousLastMessageId;
        });

        if (!newIncomingMessages.length) {
            return;
        }

        if (firstNewMessageId.value === null) {
            firstNewMessageId.value = newIncomingMessages[0].id;
        }

        newMessageIds.value = Array.from(
            new Set([...newMessageIds.value, ...newIncomingMessages.map((m) => m.id)])
        );
    };

    const scrollContainerToNewMessage = (
        container: HTMLElement,
        targetMessageId: number,
        scrollOptions: { fallbackToBottom?: boolean } = {}
    ): boolean => {
        const targetElement = container.querySelector(
            `[data-message-id="${targetMessageId}"]`
        ) as HTMLElement | null;

        if (!targetElement) {
            if (scrollOptions.fallbackToBottom) {
                options.scrollToBottom();
            }

            return false;
        }

        const containerRect = container.getBoundingClientRect();
        const targetRect = targetElement.getBoundingClientRect();
        const targetPositionInContent = targetRect.top - containerRect.top + container.scrollTop;
        const maxScroll = container.scrollHeight - container.clientHeight;

        // Align first new message to top of container (with small padding)
        const topBound = Math.max(0, targetPositionInContent - 16);
        const scrollTarget = Math.min(topBound, maxScroll);

        container.scrollTo({
            top: scrollTarget,
            behavior: 'smooth',
        });

        return true;
    };

    const scrollToNewMessages = (): void => {
        const container = options.messagesContainer.value;
        const targetMessageId = firstNewMessageId.value;

        if (!container || !targetMessageId) {
            options.scrollToBottom();
            dismiss();
            return;
        }

        const didScrollToTarget = scrollContainerToNewMessage(container, targetMessageId, {
            fallbackToBottom: true,
        });

        if (didScrollToTarget) {
            adjustScrollAfterImages(container, (el) => {
                scrollContainerToNewMessage(el, targetMessageId);
            });
        }

        dismiss();
    };

    watch(
        () => options.selectedConversation.value?.id,
        () => {
            dismiss();
        }
    );

    return {
        shouldShowNewMessageIndicator,
        newMessageCount,
        newMessageIds: computed(() => newMessageIds.value),
        firstNewMessageId: computed(() => firstNewMessageId.value),
        captureIncomingMessagesWhileAway,
        scrollToNewMessages,
        dismiss,
    };
};
